import { apiService } from "./api";

export interface ChatRequest {
  message: string;
  template?: string;
  attachments?: File[];
  isPublic?: boolean;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  projectId?: string;
  projectUrl?: string;
  estimatedTime?: string;
}

export interface FileUploadResponse {
  file_id: string;
  message: string;
  metadata: {
    filename: string;
    file_size: number;
    row_count: number;
    column_count: number;
    columns: string[];
    column_types: Record<string, string>;
    created_at: string;
    updated_at: string;
  };
  success: boolean;
}

export interface Insight {
  id: string;
  title: string;
  description: string;
  visualization_type: "bar" | "pie" | "line" | "table";
  sql_query: string;
  visualization_config: any;
  confidence: number;
}

export interface InsightsResponse {
  success: boolean;
  insights: Insight[];
}

export interface DashboardChart {
  id: string;
  title: string;
  chart_type: string;
  sql_query: string;
  position: { x: number; y: number; width: number; height: number };
  config: any;
}

export interface DashboardResponse {
  success: boolean;
  charts: DashboardChart[];
}

export interface NextStep {
  id: string;
  title: string;
  description: string;
  action_type: "drill_down" | "correlation_analysis" | "filter" | "export";
  context: any;
  priority: number;
}

export interface NextStepsResponse {
  success: boolean;
  next_steps: NextStep[];
}

export interface SQLExecutionResponse {
  success: boolean;
  results: any[];
  query_time?: number;
  row_count?: number;
}

export interface Project {
  id: string;
  title: string;
  tag: string;
  tagColor: string;
  remixes: number;
  image: string;
  bgColor: string;
  description?: string;
  author?: string;
  createdAt?: string;
}

export interface ProjectFilter {
  category?: string;
  sortBy?: "popular" | "recent" | "trending";
  limit?: number;
  offset?: number;
}

export class ExperienceAPI {
  static async submitChat(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await apiService.post(
        "/api/v1/experience/chat",
        request,
      );
      return response as ChatResponse;
    } catch (error) {
      console.error("Chat submission failed:", error);
    }
  }

  static async getProjects(filter?: ProjectFilter): Promise<Project[]> {
    try {
      const params = new URLSearchParams();
      if (filter?.category) params.append("category", filter.category);
      if (filter?.sortBy) params.append("sortBy", filter.sortBy);
      if (filter?.limit) params.append("limit", filter.limit.toString());
      if (filter?.offset) params.append("offset", filter.offset.toString());

      const response = await apiService.get(
        `/api/v1/experience/projects?${params}`,
      );
      return response as Project[];
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      return [
        {
          id: "1",
          title: "Video",
          tag: "Website",
          tagColor: "#f97316",
          remixes: 27613,
          image: "/homepage/Rectangle_1.svg",
          bgColor: "#ffffff",
          description: "Interactive video presentations",
          author: "MagicBI Team",
          createdAt: "2024-01-15",
        },
        {
          id: "2",
          title: "PPT",
          tag: "Website",
          tagColor: "#f97316",
          remixes: 16028,
          image: "/homepage/Rectangle_2.svg",
          bgColor: "#1e293b",
          description: "Interactive video presentations",
          author: "DataViz Pro",
          createdAt: "2024-01-10",
        },
        {
          id: "3",
          title: "Notion Like",
          tag: "Website",
          tagColor: "#f97316",
          remixes: 9823,
          image: "/homepage/Rectangle_3.svg",
          bgColor: "#ffffff",
          description: "Interactive video presentations",
          author: "AI Innovators",
          createdAt: "2024-01-08",
        },
        {
          id: "4",
          title: "Story",
          tag: "Website",
          tagColor: "#f97316",
          remixes: 8902,
          image: "/homepage/Rectangle_4.svg",
          bgColor: "#1e293b",
          description: "Interactive video presentations",
          author: "Crypto Masters",
          createdAt: "2024-01-05",
        },
        {
          id: "5",
          title: "Explore",
          tag: "B2B App",
          tagColor: "#64748b",
          remixes: 1156,
          image: "/homepage/Rectangle_5.svg",
          bgColor: "#ffffff",
          description: "Interactive video presentations",
          author: "FoodTech Solutions",
          createdAt: "2024-01-03",
        },
        {
          id: "6",
          title: "Workflow",
          tag: "B2B App",
          tagColor: "#64748b",
          remixes: 1122,
          image: "/homepage/Rectangle_6.svg",
          bgColor: "#1e293b",
          description: "Interactive video presentations",
          author: "Logistics Pro",
          createdAt: "2024-01-01",
        },
        // {
        //   id: "7",
        //   title: "qr-code-generator",
        //   tag: "Personal",
        //   tagColor: "#8b5cf6",
        //   remixes: 1087,
        //   image: "📱",
        //   bgColor: "#ffffff",
        //   description: "Custom QR code generator with analytics",
        //   author: "QR Master",
        //   createdAt: "2023-12-28"
        // },
        // {
        //   id: "8",
        //   title: "collabnet-visualizer",
        //   tag: "B2B App",
        //   tagColor: "#64748b",
        //   remixes: 993,
        //   image: "🕸️",
        //   bgColor: "#1e293b",
        //   description: "Collaboration network visualization tool",
        //   author: "Network Analytics",
        //   createdAt: "2023-12-25"
        // }
      ];
    }
  }

  static async getProjectById(projectId: string): Promise<Project> {
    try {
      const response = await apiService.get(
        `/api/v1/experience/projects/${projectId}`,
      );
      return response as Project;
    } catch (error) {
      console.error("Failed to fetch project:", error);
    }
  }

  static async remixProject(
    projectId: string,
  ): Promise<{ success: boolean; newProjectId: string }> {
    try {
      const response = await apiService.post(
        `/api/v1/experience/projects/${projectId}/remix`,
        {},
      );
      return response as { success: boolean; newProjectId: string };
    } catch (error) {
      console.error("Failed to remix project:", error);
    }
  }

  static async getCategories(): Promise<string[]> {
    try {
      const response = await apiService.get("/api/v1/experience/categories");
      return response as string[];
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      return [
        "Discover",
        "Internal Tools",
        "Website",
        "Personal",
        "Consumer App",
        "B2B App",
        "Prototype",
      ];
    }
  }

  static async uploadAttachment(
    file: File,
  ): Promise<{ url: string; id: string }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiService.postFormData(
        "/api/v1/file-analytics/file/demo/upload/",
        formData,
      );
      return response as { url: string; id: string };
    } catch (error) {
      console.error("Failed to upload attachment:", error);
      return {
        url: `https://dummy-upload-url.com/${file.name}`,
        id: `dummy-file-${Date.now()}`,
      };
    }
  }

  static async uploadFile(file: File): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiService.postFormData(
        "/api/v1/file-analytics/file/demo/upload/",
        formData,
      );
      return response as FileUploadResponse;
    } catch (error) {
      console.error("Failed to upload file:", error);
    }
  }

  static async getPreview(fileId: string): Promise<any> {
    try {
      const response = await apiService.get(
        `/api/v1/file-analytics/file/demo/preview/${fileId}`,
      );
      return response as any;
    } catch (error) {
      console.error("Failed to get preview:", error);
    }
  }

  static async getInsights(fileId: string): Promise<InsightsResponse> {
    try {
      const response = await apiService.get(
        `/api/v1/file-analytics/file/demo/insights/${fileId}`,
      );
      return response as InsightsResponse;
    } catch (error) {
      console.error("Failed to get insights:", error);
    }
  }

  static async getDashboards(fileId: string): Promise<DashboardResponse> {
    try {
      const response = await apiService.get(
        `/api/v1/file-analytics/file/demo/dashboards/${fileId}`,
      );
      return response as DashboardResponse;
    } catch (error) {
      console.error("Failed to get dashboards:", error);
    }
  }

  static async getNextSteps(fileId: string): Promise<NextStepsResponse> {
    try {
      const response = await apiService.get(
        `/api/v1/file-analytics/file/demo/next-steps/${fileId}`,
      );
      return response as NextStepsResponse;
    } catch (error) {
      console.error("Failed to get next steps:", error);
    }
  }

  static async executeSQL(
    fileId: string,
    sqlQuery: string,
  ): Promise<SQLExecutionResponse> {
    try {
      const response = await apiService.post(
        `/api/v1/file-analytics/file/demo/execute-sql/${fileId}`,
        { sql: sqlQuery },
      );
      return response as SQLExecutionResponse;
    } catch (error) {
      console.error("Failed to execute SQL:", error);
    }
  }
}
